<div class="page">
  <div class="shell">
    @if (loader.message) {
      <div class="loader-class" aria-live="polite">
        <div class="loader-message">{{ loader.message }}</div>
      </div>
    }

    <section class="panel" [class.collapsed]="versionsCollapsed">
      <header class="panel-header">
        <div class="panel-heading">
          <button type="button" class="toggle-button" [attr.aria-expanded]="!versionsCollapsed"
            [attr.aria-label]="(versionsCollapsed ? 'Expand' : 'Collapse') + ' Version panel'" (click)="toggleVersionsPanel()">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8"><path d="m5 7.5 5 5 5-5"/></svg>
          </button>
          <div class="title-block">
            <h2>Version</h2>
            <p>Deployed software versions per environment</p>
          </div>
        </div>
        <div class="header-badges" aria-label="Version summary">
          @for (summary of environmentSummary; track summary.name) {
            <span class="badge" [style.color]="summary.colorCode" [style.borderColor]="summary.colorCode">{{ summary.count }} {{ summary.name }}</span>
          } @empty {
            <span class="badge neutral">No versions yet</span>
          }
        </div>
      </header>

      @if (!versionsCollapsed) {
        <div class="panel-body version-wrap">
          <div class="version-button-grid">
            @for (item of featuredVersions; track item.key) {
              <button type="button" class="version-card-button" [disabled]="!item.row" (click)="openVersion(item.row)">
                @if (item.row; as row) {
                  <span class="status-corner-label" [style.color]="row.colorCode">{{ row.statusName }}</span>
                }
                <div class="version-card-label">{{ item.label }}</div>
                @if (item.row; as row) {
                  <div class="version-card-name">{{ row.label }}</div>
                  <div class="version-card-meta">{{ row.targetDate }}</div>
                } @else {
                  <div class="version-card-name">Not Available</div>
                  <div class="version-card-meta">No matching version found</div>
                }
              </button>
            }
          </div>
        </div>
      }
    </section>

    <section class="panel" [class.collapsed]="ticketsCollapsed">
      <header class="panel-header">
        <div class="panel-heading">
          <button type="button" class="toggle-button" [attr.aria-expanded]="!ticketsCollapsed"
            [attr.aria-label]="(ticketsCollapsed ? 'Expand' : 'Collapse') + ' Tickets panel'" (click)="toggleTicketsPanel()">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8"><path d="m5 7.5 5 5 5-5"/></svg>
          </button>
          <div class="title-block">
            <h2>Tickets</h2>
            <p>{{ subtext }}</p>
          </div>
        </div>
        <div class="top-new-group" aria-label="Ticket summary">
          @if (attentionCount > 0) {
            <span class="new-tag">{{ attentionCount }} NEW</span>
          }
          <span class="open-count">{{ openItemsCount }} open</span>
        </div>
      </header>

      @if (!ticketsCollapsed) {
        <div class="panel-body tickets-content">
          <section class="metric-grid" aria-label="Ticket metrics">
            @for (card of cards; track card.label; let i = $index) {
              <article class="metric-card"
                [class.attention]="cardVariant(card) === 'attention'"
                [class.validation]="cardVariant(card) === 'validation'"
                [class.success]="cardVariant(card) === 'success'">
                <div class="metric-icon">
                  @switch (cardVariant(card)) {
                    @case ('attention') {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="8"/><path d="M12 8v5M12 16.2h.01"/></svg>
                    }
                    @case ('validation') {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="8"/><path d="M12 8v5M12 16.2h.01"/></svg>
                    }
                    @case ('success') {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="8"/><path d="m8.5 12 2.2 2.2 4.8-5"/></svg>
                    }
                    @default {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="8"/><path d="M9.5 9.5v5M14.5 9.5v5"/></svg>
                    }
                  }
                </div>
                <button type="button" class="metric-value metric-value-link" (click)="openCardTickets(card, i)"
                  [disabled]="card.count === 0" aria-haspopup="dialog"
                  [attr.aria-label]="'View ' + card.count + ' ' + card.label + ' tickets'">
                  {{ card.count }}
                </button>
                <div class="metric-label" [title]="card.info">{{ card.label }}</div>
              </article>
            } @empty {
              <article class="metric-card">
                <p class="metric-value">0</p>
                <div class="metric-label">No metrics available</div>
              </article>
            }
          </section>

          <section class="lists-grid">
            @for (list of lists; track list.label) {
              <article class="list-card">
                <header class="list-card-header">
                  <div class="list-title" [title]="list.info">
                    <svg class="list-icon" [class.purple]="isActivityList(list)" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.7"><circle cx="10" cy="10" r="6"/><circle cx="10" cy="10" r="2"/></svg>
                    {{ list.label }}
                  </div>
                  <span class="badge" [class.purple]="isActivityList(list)" [class.blue]="!isActivityList(list)">{{ list.items.length }} items</span>
                </header>
                <ul class="issue-list">
                  @for (item of list.items; track item.feedback_id) {
                    <li class="issue-item">
                      <button type="button" class="issue-item-button" (click)="openTicketById(item.feedback_id)">
                        <span class="severity-dot light"></span>
                        <div>
                          <div class="issue-title">{{ item.title }}</div>
                          <div class="issue-meta">#{{ item.feedback_id }}</div>
                        </div>
                      </button>
                    </li>
                  } @empty {
                    <li class="issue-item"><div class="issue-empty">No items.</div></li>
                  }
                </ul>
              </article>
            } @empty {
              <article class="list-card">
                <ul class="issue-list">
                  <li class="issue-item"><div class="issue-empty">No lists available.</div></li>
                </ul>
              </article>
            }
          </section>
        </div>
      }
    </section>
  </div>
  <dialog #ticketDialog class="ticket-popup" aria-labelledby="ticket-popup-heading"
    (click)="onTicketDialogClick($event)" (close)="closeCardTickets()">
    <header class="ticket-popup-header">
      <div>
        <h3 id="ticket-popup-heading">{{ selectedCard?.label }}</h3>
      </div>
      <button type="button" class="ticket-popup-close" aria-label="Close tickets" (click)="closeCardTickets()">×</button>
    </header>
    @if (selectedCard) {
      @if (drilldownLoading) {
        <p class="ticket-popup-notice" aria-live="polite">Loading tickets…</p>
      } @else {
        <ul class="ticket-popup-list">
          @for (ticket of drilldownTickets; track ticket.feedback_id) {
            <li>
              <button type="button" class="ticket-popup-item" (click)="openTicketFromPopup(ticket.feedback_id)">
                <span class="ticket-popup-id">#{{ ticket.feedback_id }}</span>
                <span class="ticket-popup-name">{{ ticket.title }}</span>
                <span aria-hidden="true">›</span>
              </button>
            </li>
          } @empty {
            <li class="ticket-popup-empty">No tickets available.</li>
          }
        </ul>
        @if (drilldownTotalPages > 1) {
          <footer class="ticket-popup-pagination">
            <button type="button" (click)="goToDrilldownPage(drilldownPage - 1)" [disabled]="drilldownPage <= 1">Previous</button>
            <span>Page {{ drilldownPage }} of {{ drilldownTotalPages }}</span>
            <button type="button" (click)="goToDrilldownPage(drilldownPage + 1)" [disabled]="drilldownPage >= drilldownTotalPages">Next</button>
          </footer>
        }
      }
    }
  </dialog>

</div>